/**
 * Row-to-row shading, ported from the engine so the viewer cannot disagree with it.
 *
 * Every function here mirrors one on the server:
 *
 *   projectedSolarZenithAngle  <- pvlib.shading.projected_solar_zenith_angle
 *   shadedFraction1d           <- pvlib.shading.shaded_fraction1d (Anderson &
 *                                 Jensen 2024, Eq. 12)
 *   calcAxisTilt / calcCrossAxisTilt
 *                              <- pvlib.tracking, the same pair
 *                                 effective_irradiance._near_shading_loss calls
 *   verticalSegments / bypassedFraction / beamLossFraction /
 *   incrementalElectricalLoss  <- pv_core/row_shading.py, function for function
 *   trackerRotation            <- pvlib.tracking.singleaxis (rotation only)
 *
 * The point of porting rather than approximating: a picture that shows one thing
 * while the yield model computes another is worse than no picture. The values
 * are checked against pvlib in rowShading.test.js, against a fixture generated
 * by scripts/generate_shading_fixture.py in the backend repo.
 *
 * Angles in degrees, lengths in metres, azimuths clockwise from north.
 */

const DEG = Math.PI / 180;
const sind = (d) => Math.sin(d * DEG);
const cosd = (d) => Math.cos(d * DEG);
const tand = (d) => Math.tan(d * DEG);
const degrees = (r) => r / DEG;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export const DEFAULT_SUBSTRINGS_PER_MODULE = 3;
export const DEFAULT_ELECTRICAL_EFFECT = 1.0;

/**
 * Solar zenith projected onto the plane normal to the row axis.
 *
 * Signed: negative before the sun crosses the row plane, positive after, which
 * is what tells shaded_fraction1d and the tracker which side the shadow is on.
 */
export function projectedSolarZenithAngle(
  solarZenith,
  solarAzimuth,
  axisTilt,
  axisAzimuth
) {
  const sinZ = sind(solarZenith);
  const sx = sinZ * sind(solarAzimuth);
  const sy = sinZ * cosd(solarAzimuth);
  const sz = cosd(solarZenith);
  const sxPrime = sx * cosd(axisAzimuth) - sy * sind(axisAzimuth);
  const szPrime =
    sx * sind(axisAzimuth) * sind(axisTilt) +
    sy * sind(axisTilt) * cosd(axisAzimuth) +
    sz * cosd(axisTilt);
  return degrees(Math.atan2(sxPrime, szPrime));
}

/**
 * Fraction of a row's slant height shadowed by the row in front.
 *
 * Anderson & Jensen (2024) Eq. 12. Returns 0 when the sun is high enough to
 * clear the row in front, 1 when the row is fully shadowed.
 */
export function shadedFraction1d({
  solarZenith,
  solarAzimuth,
  axisAzimuth,
  shadedRowRotation,
  collectorWidth,
  pitch,
  axisTilt = 0,
  surfaceToAxisOffset = 0,
  crossAxisSlope = 0,
  shadingRowRotation = null,
}) {
  if (!(collectorWidth > 0) || !(pitch > 0)) return 0;
  const theta1 = shadingRowRotation == null ? shadedRowRotation : shadingRowRotation;
  const psz = projectedSolarZenithAngle(
    solarZenith,
    solarAzimuth,
    axisTilt,
    axisAzimuth
  );

  const d1 = theta1 - psz;
  const d2 = shadedRowRotation - psz;
  const dSlope = psz - crossAxisSlope;
  const cos2 = Math.abs(cosd(d2));

  const tStar =
    0.5 +
    Math.abs(cosd(d1)) / cos2 / 2 +
    (Math.sign(psz) * surfaceToAxisOffset) / collectorWidth / cos2 *
      (sind(d2) - sind(d1)) -
    (pitch / collectorWidth) * cosd(dSlope) / cos2 / cosd(crossAxisSlope);

  return Number.isFinite(tStar) ? clamp(tStar, 0, 1) : 0;
}

/** pvlib.tracking.calc_axis_tilt: tilt of the row axis on sloped ground. */
export function calcAxisTilt(slopeAzimuth, slopeTilt, axisAzimuth) {
  const dGamma = axisAzimuth - slopeAzimuth;
  return degrees(Math.atan(cosd(dGamma) * tand(slopeTilt)));
}

/** pvlib.tracking.calc_cross_axis_tilt: terrain slope measured ACROSS the rows. */
export function calcCrossAxisTilt(slopeAzimuth, slopeTilt, axisAzimuth, axisTilt) {
  const dg = axisAzimuth - slopeAzimuth;
  const cosBa = cosd(axisTilt);
  const cosBg = cosd(slopeTilt);
  const sinBg = sind(slopeTilt);
  const sinDg = sind(dg);
  const vx = sinDg * cosBa * cosBg;
  const vy = sind(axisTilt) * sinBg + cosd(dg) * cosBa * cosBg;
  const vz = -sinDg * sinBg * cosBa;
  const vnorm = Math.sqrt(vx * vx + vy * vy + vz * vz);
  if (!(vnorm > 0)) return 0;
  return degrees(
    Math.asin(
      clamp(
        ((vx * cosd(dg) - vy * sind(dg)) * sind(axisTilt) + vz * cosBa) / vnorm,
        -1,
        1
      )
    )
  );
}

/**
 * Tilt of the row axis, from the project's terrain figures.
 *
 * The component of the ground slope that runs ALONG the rows. Zero on flat or
 * unknown terrain. Its partner is crossAxisSlopeFor below, and the two are
 * passed to shadedFraction1d together: pvlib's calc_cross_axis_tilt takes this
 * angle as an input, so a caller holding one already holds the other.
 */
export function axisTiltFor({ slopeTilt, slopeAzimuth, axisAzimuth }) {
  const tilt = Number(slopeTilt);
  if (!(tilt > 0) || slopeAzimuth == null || !Number.isFinite(Number(slopeAzimuth))) {
    return 0;
  }
  return calcAxisTilt(Number(slopeAzimuth), tilt, axisAzimuth);
}

/**
 * Cross-axis slope the engine would use, from the project's terrain figures.
 *
 * Mirrors effective_irradiance._near_shading_loss: zero on flat or unknown
 * terrain, which is exactly the flat-ground behaviour.
 */
export function crossAxisSlopeFor({ slopeTilt, slopeAzimuth, axisAzimuth }) {
  const tilt = Number(slopeTilt);
  if (!(tilt > 0) || slopeAzimuth == null || !Number.isFinite(Number(slopeAzimuth))) {
    return 0;
  }
  const az = Number(slopeAzimuth);
  const axisTilt = calcAxisTilt(az, tilt, axisAzimuth);
  return calcCrossAxisTilt(az, tilt, axisAzimuth, axisTilt);
}

/**
 * Single-axis tracker rotation, with backtracking and the rotation limit.
 *
 * pvlib.tracking.singleaxis, rotation only - the viewer needs the angle the row
 * stands at, not the full irradiance chain. Returns null when the sun is below
 * the horizon, where the tracker angle is undefined (pvlib returns NaN).
 */
export function trackerRotation({
  solarZenith,
  solarAzimuth,
  axisTilt = 0,
  axisAzimuth = 180,
  maxAngle = 90,
  backtrack = true,
  gcr = 0.35,
  crossAxisTilt = 0,
}) {
  if (solarZenith > 90) return null;
  const omegaIdeal = projectedSolarZenithAngle(
    solarZenith,
    solarAzimuth,
    axisTilt,
    axisAzimuth
  );
  let theta = omegaIdeal;
  if (backtrack && gcr > 0) {
    const axesDistance = 1 / (gcr * cosd(crossAxisTilt));
    const temp = Math.abs(axesDistance * cosd(omegaIdeal - crossAxisTilt));
    if (temp < 1) {
      theta = omegaIdeal + degrees(-Math.sign(omegaIdeal) * Math.acos(temp));
    }
  }
  const limit = Math.abs(maxAngle);
  return clamp(theta, -limit, limit);
}

/**
 * How many independently bypassable sections stack up ONE module.
 *
 * See pv_core/row_shading.py: portrait full-cell is 1 (the whole module drops
 * out), portrait half-cut is 2 (the halves are in parallel), landscape gives one
 * band per diode - halved when a half-cut module shares its diodes across two
 * side-by-side halves.
 */
export function verticalSegments({
  portrait = true,
  halfCut = true,
  bypassDiodes = DEFAULT_SUBSTRINGS_PER_MODULE,
} = {}) {
  const d = Math.max(1, Math.trunc(bypassDiodes || DEFAULT_SUBSTRINGS_PER_MODULE));
  if (portrait) return halfCut ? 2 : 1;
  return halfCut && d >= 2 * DEFAULT_SUBSTRINGS_PER_MODULE
    ? Math.max(1, Math.trunc(d / 2))
    : d;
}

/** Fraction of the row switched out by bypass diodes, quantised with ceil. */
export function bypassedFraction(shadedFraction, opts = {}) {
  const {
    verticalModules = 1,
    portrait = true,
    halfCut = true,
    bypassDiodes = DEFAULT_SUBSTRINGS_PER_MODULE,
  } = opts;
  const fs = clamp(Number(shadedFraction) || 0, 0, 1);
  if (fs <= 1e-9) return 0;
  const steps =
    Math.max(1, Math.trunc(verticalModules || 1)) *
    verticalSegments({ portrait, halfCut, bypassDiodes });
  return clamp(Math.ceil(fs * steps) / steps, 0, 1);
}

/** Fraction of BEAM output lost: p * bypassed + (1 - p) * shaded. */
export function beamLossFraction(shadedFraction, opts = {}) {
  const {
    model = "bypass",
    electricalEffect = DEFAULT_ELECTRICAL_EFFECT,
  } = opts;
  const fs = clamp(Number(shadedFraction) || 0, 0, 1);
  const resolved = ["bypass", "linear", "none"].includes(String(model))
    ? String(model)
    : "bypass";
  if (resolved === "none") return 0;
  if (resolved === "linear") return fs;
  const byp = bypassedFraction(fs, opts);
  const p = clamp(Number(electricalEffect ?? 1), 0, 1);
  return clamp(p * byp + (1 - p) * fs, 0, 1);
}

/**
 * The electrical excess alone: (loss - shaded) / (1 - shaded).
 *
 * What the engine actually applies, because infinite_sheds has already removed
 * the geometrically shaded beam and applying the whole loss would count the
 * linear part twice.
 */
/**
 * Beam lost to an imported obstacle, as a fraction of the array's beam.
 *
 * Mirrors pv_core/obstacle_shading.py::beam_loss. Keep the two in step: a
 * viewer that disagrees with the engine about the same stored table is worse
 * than either alone.
 *
 * The two shaded fractions in this model measure different things. The row
 * fraction is a share of ONE row's slant height, which is what the bypass model
 * is written for. A ray-cast obstacle reading is a share of the WHOLE field's
 * module area. Passing the second through the quantiser charged a tree over 2%
 * of a field as half the array's beam, because a portrait half-cut row has two
 * bypass steps and any non-zero fraction rounds up to the first.
 *
 * So the worst affected row is quantised -- inside a row the diodes really do
 * switch out -- and the result is weighted by how much of the field is affected
 * at all. Without a per-row reading the honest fallback is linear.
 *
 * @param {number} overall   blocked share of total module area
 * @param {number} worstRow  blocked share of the worst single row, of that row
 */
export function obstacleBeamLoss(overall, worstRow, opts = {}) {
  const field = Math.min(1, Math.max(0, Number(overall) || 0));
  const worst = Math.min(1, Math.max(0, Number(worstRow) || 0));
  if (!(worst > 1e-9)) return field;
  const affected = Math.min(1, Math.max(0, field / worst));
  return Math.min(1, Math.max(field, affected * beamLossFraction(worst, opts)));
}


export function incrementalElectricalLoss(shadedFraction, opts = {}) {
  const fs = clamp(Number(shadedFraction) || 0, 0, 1);
  const total = beamLossFraction(fs, opts);
  const remaining = 1 - fs;
  if (!(remaining > 1e-9)) return 0;
  return clamp((total - fs) / remaining, 0, 1);
}
